import React, { useState, useEffect, useCallback } from 'react';
import useStockStore from '../../../stores/useStockStore';
import AutomatedTradingDisclaimerModal from './AutomatedTradingDisclaimerModal';
import { db } from '../../../services/firebase';
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth } from '../../../services/firebase';
import { Trash2 } from 'lucide-react';

const AddWatchlistModal = () => {
  const {
    setShowAddModal,
    updateStocksFromForm,
    updateStockStatus,
    setShowRiskDisclosure,
    showRiskDisclosure,
    setShowDisclaimerModal,
    showDisclaimerModal,
    updateStockquanlity,
    stocks,
    showAllSymbolsSelectedPopup,
    setShowAllSymbolsSelectedPopup,
    showAddModal,
    setAutomatedSymbols,
    setSelectedSymbols,
  } = useStockStore();

  // Scroll to top when modal opens and prevent body scroll
  useEffect(() => {
    if (showAddModal) {
      window.scrollTo(0, 0);
      
      // Store current scroll position
      const scrollY = window.scrollY;
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      // Also prevent scroll on html element
      document.documentElement.style.overflow = 'hidden';

      // Debug logging removed to prevent infinite re-renders
    } else {
      // Restore body scroll when modal is closed
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.documentElement.style.overflow = '';
      
      // Restore scroll position
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }

      // Debug logging removed to prevent infinite re-renders
    }

    // Cleanup function to restore body scroll when component unmounts
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.documentElement.style.overflow = '';
    };
  }, [showAddModal]);

  // Debug logging removed to prevent infinite re-renders

  const getCapitalPerUnit = (symbol, type) => {
    if (symbol === 'SPX') return type === 'single-leg' ? 4500 : 250;
    if (symbol === 'SPY' || symbol === 'QQQ') return type === 'single-leg' ? 450 : 200;
    return 0;
  };

  const [userTier, setUserTier] = useState(null); // Store user tier: 'tier1', 'tier2', 'tier3', or null

  // Fetch user tier from Firestore when modal opens
  useEffect(() => {
    const fetchUserTier = async () => {
      if (showAddModal && auth.currentUser?.uid) {
        try {
          const userDocRef = doc(db, 'users', auth.currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const rawTier = userData.tier;
            // Normalize tier value (case-insensitive, handle variations)
            const normalizedTier = rawTier ? rawTier.toLowerCase().trim() : null;
            setUserTier(normalizedTier);
            console.log('User tier fetched:', {
              rawTier,
              normalizedTier,
              maxTrade: normalizedTier === 'tier3' ? 2000 : normalizedTier === 'tier2' ? 1000 : 500
            });
          } else {
            // User not found, use default tier1
            console.log('User document not found, using default tier1');
            setUserTier(null);
          }
        } catch (error) {
          console.error('Error fetching user tier:', error);
          setUserTier(null);
        }
      }
    };

    fetchUserTier();
  }, [showAddModal]);

  const [formData, setFormData] = useState([
    { symbol: '', type: '', quanlity: '', value: 0, status: 'Pending' },
  ]);

  const [availableSymbols, setAvailableSymbols] = useState([
    ['SPX', 'SPY', 'QQQ'],
  ]);

  const updateAvailableSymbols = useCallback((currentFormData) => {
    const allSymbols = ['SPX', 'SPY', 'QQQ'];
    const engagedSymbols = stocks
      .filter(stock => stock.status && stock.status !== '')
      .map(stock => stock.symbol);

    const formSymbols = currentFormData.map(item => item.symbol);

    const newAvailableSymbols = currentFormData.map((item, index) => {
      const symbolsSelectedInOtherRows = formSymbols.filter((_, i) => i !== index && formSymbols[i] !== '');
      const availableForRow = allSymbols.filter(symbol =>
        !engagedSymbols.includes(symbol) && !symbolsSelectedInOtherRows.includes(symbol)
      );
      if (item.symbol && item.symbol !== '' && !engagedSymbols.includes(item.symbol)) {
        const uniqueSymbols = new Set([...availableForRow, item.symbol]);
        return Array.from(uniqueSymbols).sort();
      }

      return availableForRow.sort();
    });

    if (currentFormData.length < 3) {
      const symbolsAlreadyInForm = formSymbols.filter(symbol => symbol !== '');
      const availableForNewRow = allSymbols.filter(symbol =>
        !engagedSymbols.includes(symbol) && !symbolsAlreadyInForm.includes(symbol)
      );
      newAvailableSymbols.push(availableForNewRow.sort());
    }

    setAvailableSymbols(newAvailableSymbols);
  }, [stocks]);

  // Re-validate formData values when userTier changes
  useEffect(() => {
    if (userTier !== null) {
      setFormData(prevFormData => {
        const updated = [...prevFormData];
        let hasChanges = false;
        
        updated.forEach((item, index) => {
          if (item.symbol && item.type && item.quanlity) {
            const limits = getMinMaxTrade(item.symbol, item.type);
            const currentValue = parseInt(item.quanlity, 10);
            
            if (!isNaN(currentValue) && currentValue > limits.max) {
              updated[index].quanlity = limits.max;
              hasChanges = true;
            }
          }
        });
        
        if (hasChanges) {
          // Update available symbols with the new form data
          updateAvailableSymbols(updated);
          return updated;
        }
        
        return prevFormData;
      });
    }
  }, [userTier, updateAvailableSymbols]); // Include updateAvailableSymbols since it's now memoized

  const getMinMaxTrade = (symbol, type) => {
    // Normalize tier (case-insensitive, handle variations)
    const normalizedTier = userTier?.toLowerCase()?.trim();

    // Determine max based on user tier
    let maxTrade = 500; // Default for tier1 or no tier
    if (normalizedTier === 'tier2') {
      maxTrade = 1000;
    } else if (normalizedTier === 'tier3') {
      maxTrade = 2000;
    }

    // Debug logging to help diagnose tier issues
    if (normalizedTier) {
      console.log('Tier validation:', {
        rawTier: userTier,
        normalizedTier,
        maxTrade,
        symbol,
        type
      });
    }

    if (symbol === 'SPX') {
      return type === 'single-leg' ? { min: 0, max: maxTrade } : { min: 0, max: maxTrade };
    }
    if (symbol === 'SPY' || symbol === 'QQQ') {
      return type === 'single-leg' ? { min: 0, max: maxTrade } : { min: 0, max: maxTrade };
    }
    return { min: 0, max: maxTrade };
  };


  const recalculateCapital = (data, index) => {
    const { symbol, type, quanlity } = data[index];
    if (symbol && type && quanlity) {
      const capitalPerUnit = getCapitalPerUnit(symbol, type);
      data[index].value = capitalPerUnit * quanlity;
    }
  };

  const recalculateQuantity = (data, index) => {
    const { symbol, type, value } = data[index];
    if (symbol && type && value) {
      const capitalPerUnit = getCapitalPerUnit(symbol, type);
      if (capitalPerUnit > 0) {
        data[index].quanlity = Math.round(value / capitalPerUnit);
      }
    }
  };

  const handleInputChange = (field, value, index) => {
    const updated = [...formData];
    updated[index][field] = value;
    
    if (field === 'quanlity') {
      recalculateCapital(updated, index);
    } else if (field === 'value') {
      recalculateQuantity(updated, index);
    } else if (field === 'type') {
      // Recalculate capital when type changes
      recalculateCapital(updated, index);
    }
    
    setFormData(updated);
    updateAvailableSymbols(updated);
  };

  const handleSymbolChange = (symbol, index) => {
    const updated = [...formData];
    updated[index].symbol = symbol;
    // If SPX is selected, force Type to 'spread'
    if (symbol === 'SPX') {
      updated[index].type = 'spread';
    }
    recalculateCapital(updated, index);
    setFormData(updated);
    updateAvailableSymbols(updated);
  };

  const isRowComplete = ({ symbol, type, quanlity }) => symbol && type && quanlity;

  const addNewRow = () => {
          const newForm = [
        ...formData,
        { symbol: '', type: '', quanlity: '', value: 0, status: 'Pending' },
      ];
    setFormData(newForm);
    updateAvailableSymbols(newForm);
  };

  const calculateTotalCapital = () => {
    return formData.reduce((total, item) => total + (item.quanlity || 0), 0);
  };

  const validateForm = () => {
    const incompleteRows = [];
    
    formData.forEach((item, index) => {
      const missingFields = [];
      
      if (!item.symbol || item.symbol === '') {
        missingFields.push('Symbol');
      }
      if (!item.type || item.type === '') {
        missingFields.push('Type');
      }
      if (!item.quanlity || item.quanlity === '') {
        missingFields.push('$Max/Trade');
      }
      
      if (missingFields.length > 0) {
        incompleteRows.push({
          row: index + 1,
          missingFields: missingFields
        });
      }
    });
    
    return incompleteRows;
  };

  const handleEngageAutomation = () => {
    // Validate form first
    const validationErrors = validateForm();
    
    if (validationErrors.length > 0) {
      // Create validation message
      let message = 'Please fill in all required fields:\n\n';
      validationErrors.forEach(error => {
        message += `${error.missingFields.join(', ')}\n`;
      });
      
      setValidationMessage(message);
      setShowValidationPopup(true);
      return;
    }

    // Ensure all values are calculated before creating stocksToUpdate
    const updatedFormData = [...formData];
    updatedFormData.forEach((item, index) => {
      if (item.symbol && item.type && item.quanlity) {
        recalculateCapital(updatedFormData, index);
      }
    });

    const stocksToUpdate = updatedFormData
      .filter(isRowComplete)
      .map((item) => ({
        symbol: item.symbol,
        value: item.quanlity, // Use quanlity as value to display
        type: item.type,
        spread: item.type === 'spread' ? 2 : 1,
        hedge: false,
        status: 'Pending',
        quanlity: item.quanlity,
      }));

    // Store the stocks data temporarily
    window.tempStocksData = stocksToUpdate;
    
    // Show risk disclosure first
    setShowRiskDisclosure(true);
  };

  const handleActivate = async () => {
    // Just show the disclaimer modal without updating stocks
    setShowRiskDisclosure(false);
    setShowDisclaimerModal(true);
  };

  const closeRiskDisclosure = () => {
    setShowRiskDisclosure(false);
    delete window.tempStocksData;
  };

  const closeModal = () => {
    setShowAddModal(false);
    delete window.tempStocksData;
  };

  const [limitTotalTrades, setLimitTotalTrades] = useState(6);
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  useEffect(() => {
    updateAvailableSymbols(formData);
  }, [stocks]);

  // Reset the form to a single empty row whenever the modal opens
  useEffect(() => {
    if (showAddModal) {
      const resetForm = [{ symbol: '', type: '', quanlity: '', value: 0, status: 'Pending' }];
      setFormData(resetForm);
      updateAvailableSymbols(resetForm);
    }
  }, [showAddModal]);

  const handleRemoveRow = (index) => {
    const updated = [...formData];
    updated.splice(index, 1);
    setFormData(updated);
    updateAvailableSymbols(updated);
  };

  return (
    <>
      {/* Main Modal */}
      {showAddModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center px-4"
          onClick={(e) => {
            // Close modal when clicking on backdrop
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
          onWheel={(e) => {
            // Prevent scroll on backdrop
            e.preventDefault();
            e.stopPropagation();
          }}
          onTouchMove={(e) => {
            // Prevent touch scroll on backdrop
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div 
            className="bg-[#0F172A] text-white p-4 rounded-xl w-full max-w-3xl shadow-lg relative z-10 max-h-[95vh] overflow-y-auto"
            onClick={(e) => {
              // Prevent clicks inside the modal from bubbling up
              e.stopPropagation();
            }}
            onWheel={(e) => {
              // Allow scroll only within modal content
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              // Allow touch scroll only within modal content
              e.stopPropagation();
            }}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <h2 className="text-xl font-bold mb-4 text-center">SELECT ETF FOR AUTOMATION</h2>

            <div className="mb-4">
              <label className="block text-xs mb-1">Select algo to activate</label>
              <select className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-xs" defaultValue="Quantum Flo Auto Algo">
                <option disabled>Quantum Flo Auto Algo</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 pb-4 text-xs">
              <div className="font-semibold">Symbol</div>
              <div className="font-semibold">Type</div>
              <div className="font-semibold">$Max/Trade</div>
            </div>

            {formData.map((item, index) => {
              const shouldDisplay = index === 0 || formData.slice(0, index).every(isRowComplete);
              return shouldDisplay ? (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 pb-4 text-xs">
                  <div>
                    <select
                      className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-xs"
                      value={item.symbol}
                      onChange={(e) => handleSymbolChange(e.target.value, index)}
                    >
                      <option value="">Select</option>
                      {availableSymbols[index]?.map((symbol) => (
                        <option key={symbol} value={symbol}>{symbol}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <select
                      className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-xs"
                      value={item.type}
                      onChange={(e) => handleInputChange('type', e.target.value, index)}
                    >
                      <option value="">Select</option>
                      <option value="single-leg" disabled={item.symbol === 'SPX'}>Single Leg</option>
                      <option value="spread">Spread</option>
                    </select>
                  </div>

                  <div className="relative w-full flex items-center gap-2">
                    <div className="relative flex-1">
                      <input
                        key={`max-trade-${index}`}
                        type="text"
                        className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-xs pl-6 pr-4"
                        value={item.quanlity}
                        defaultValue=""
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          
                          // Allow empty string for clearing
                          if (inputValue === '') {
                            handleInputChange('quanlity', '', index);
                            return;
                          }
                          
                          const val = parseInt(inputValue, 10);
                          if (isNaN(val)) return;
                          
                          // Check only maximum limit during typing
                          const limits = item.symbol && item.type ? getMinMaxTrade(item.symbol, item.type) : getMinMaxTrade('SPY', 'single-leg');
                          if (val > limits.max) {
                            handleInputChange('quanlity', limits.max, index);
                          } else {
                            // Allow any value including 0 and below minimum
                            handleInputChange('quanlity', val, index);
                          }
                        }}
                        onBlur={(e) => {
                          // Validate and correct on blur
                          const val = parseInt(e.target.value, 10);
                          if (isNaN(val) || val === '' || e.target.value === '') {
                            // Leave empty - no default value
                            handleInputChange('quanlity', '', index);
                            return;
                          }
                          
                          // Apply limits based on symbol and type
                          const limits = item.symbol && item.type ? getMinMaxTrade(item.symbol, item.type) : getMinMaxTrade('SPY', 'single-leg');
                          if (val > limits.max) {
                            handleInputChange('quanlity', limits.max, index);
                          }
                        }}
                        placeholder=""
                      />
                      <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                        $
                      </span>
                    </div>
                    {formData.length > 1 && (
                      <button
                        onClick={() => handleRemoveRow(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Remove row"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ) : null;
            })}

            {formData.length < 3 &&
              formData.every(isRowComplete) &&
              availableSymbols[formData.length] &&
              availableSymbols[formData.length].length > 0 && (
                <button
                  onClick={addNewRow}
                  className="mt-4 text-blue-500 hover:underline text-xs"
                >
                  + Add Another ETF
                </button>
              )}

            <div className="mt-6 pt-4 border-t border-gray-700">
              <div className="flex items-center mb-4 text-xs">
                <span className="font-semibold">Total Estimated Capital Required: </span>
                <span className="ml-2">${calculateTotalCapital().toFixed(2)}</span>
              </div>
              <div className="flex items-center text-xs mb-4">
                <label htmlFor="limitTrades" className="font-semibold">Maximum Number of Trades Simultaneously: </label>
                <input
                  id="limitTrades"
                  type="number"
                  min="1"
                  max="6"
                  step="1"
                  className="w-20 bg-gray-800 border border-gray-700 rounded p-1 text-xs ml-2"
                  value={limitTotalTrades}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (Number.isInteger(val) && val >= 1 && val <= 6) {
                      setLimitTotalTrades(val);
                    }
                  }}
                />
              </div>
            </div>

            <div className="text-xs text-gray-400 mb-4">
              QF Auto Algo will not trade if your account does not have sufficient buying power.
            </div>

            <div className="mt-6 flex justify-between gap-4">
              <button
                onClick={handleEngageAutomation}
                disabled={!formData.some(isRowComplete) || calculateTotalCapital() <= 0}
                className={`w-full py-2 rounded font-semibold ${!formData.some(isRowComplete) || calculateTotalCapital() <= 0 ? 'bg-gray-600 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
              >
                Engage Automation
              </button>
              <button
                onClick={closeModal}
                className="w-full py-2 bg-red-600 text-white rounded font-semibold hover:bg-red-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Risk Disclosure Modal */}
      {showRiskDisclosure && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center px-4">
          <div className="bg-[#0F172A] text-white p-6 rounded-xl w-full max-w-4xl shadow-lg relative z-10">
            <h2 className="text-3xl font-bold mb-4 text-center">Risk Disclosure & Waiver of Liability</h2>

            <div className="overflow-y-auto max-h-96 text-sm leading-relaxed space-y-3">
              <p className="font-bold">
                WARNING: Algorithmic trading is inherently risky. Do not proceed unless you fully understand and accept the risks outlined below. By continuing, you confirm that you have read, understood, and voluntarily accept full responsibility for any and all outcomes resulting from your use of JTNC’s Quantum Flo Auto Algo.
              </p>

              <div>
                <h3 className="font-semibold mb-1">General Trading Risk</h3>
                <p className="mb-1">Trading financial markets, particularly using automated systems, carries significant risk of loss. The use of algorithms does not guarantee profits, and past performance is not indicative of future results. By engaging JTNC’s Quantum Flo Auto Algo, you acknowledge that:</p>
                <ul className="list-disc list-outside ml-6 space-y-1 marker:text-white">
                  <li>You may lose some or all of your capital.</li>
                  <li>You assume full responsibility for monitoring your account.</li>
                  <li>No level of automation can eliminate market risk, volatility, or black swan events.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-1">User Responsibility for Configuration</h3>
                <p className="mb-1">You are solely responsible for configuring JTNC’s Quantum Flo Auto Algo, including:</p>
                <ul className="list-disc list-outside ml-6 space-y-1 marker:text-white">
                  <li>Choosing the symbol(s) to trade (SPY, QQQ, and/or SPX, as offered in the engagement flow).</li>
                  <li>Setting contract limits, account size parameters, and any broker-level settings.</li>
                  <li>Ensuring your brokerage account is properly approved and funded.</li>
                </ul>
                <p className="mt-1">JTNC Group, LLC. assumes no liability for misconfigured settings, input errors, or inappropriate use of the system based on your financial situation or account structure.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-1">Understanding Algorithm Behavior</h3>
                <p className="mb-1">By proceeding, you acknowledge that:</p>
                <ul className="list-disc list-outside ml-6 space-y-1 marker:text-white">
                  <li>You understand how JTNC’s Quantum Flo Auto Algo enters and exits trades, including its reliance on market range forecasting, options flow, and sentiment modeling.</li>
                  <li>You are aware that JTNC’s Quantum Flo Auto Algo will autonomously execute trades in your account without additional manual confirmation (after your daily opt-in, or during any period covered by a Standing Trading Authorization you have signed).</li>
                  <li>You retain full control to pause, disable, or override trades at any time via your brokerage interface.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-1">Market Risk & Volatility</h3>
                <p className="mb-1">JTNC’s Quantum Flo Auto Algo does not provide protection from:</p>
                <ul className="list-disc list-outside ml-6 space-y-1 marker:text-white">
                  <li>Sudden market volatility or flash crashes.</li>
                  <li>Overnight gaps (though trades are typically not held overnight or over weekends).</li>
                  <li>Macro events, geopolitical shocks, or unexpected news.</li>
                </ul>
                <p className="mt-1">All trades are subject to real-time market dynamics, including slippage and price dislocation.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-1">Third-Party Broker & Data Integration</h3>
                <p className="mb-1">JTNC’s Quantum Flo Auto Algo connects to your brokerage account via authorized API access. As such:</p>
                <ul className="list-disc list-outside ml-6 space-y-1 marker:text-white">
                  <li>JTNC Group, LLC is not responsible for execution, latency, or performance issues stemming from the broker (currently Tradier and Tastytrade; other brokers as offered from time to time).</li>
                  <li>You acknowledge that data feeds, execution pathways, and account access are managed by your broker and outside the direct control of JTNC Group, LLC.</li>
                  <li>Any disconnections, trading halts, or order routing issues must be resolved directly with your brokerage firm.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-1">Execution Delays or Failures</h3>
                <p className="mb-1">Automated trading is subject to technological limitations. JTNC’s Quantum Flo Auto Algo does not and cannot guarantee:</p>
                <ul className="list-disc list-outside ml-6 space-y-1 marker:text-white">
                  <li>Immediate or successful order execution.</li>
                  <li>Fill quality, slippage control, or priority routing.</li>
                  <li>Uninterrupted service in the event of outages, connectivity issues, or broker downtime.</li>
                  <li>Performance which matches other client fills and/or performance.</li>
                </ul>
              </div>

              <p>
                By continuing, you agree to all of the above. You voluntarily release JTNC Group, LLC, its affiliates, employees, and contractors from any liability for losses, damages, or system behavior resulting from the use of JTNC’s Quantum Flo Auto Algo and confirm that you are using this service at your own risk.
              </p>
            </div>

            <div className="flex justify-between mt-6 space-x-2">
              <button
                onClick={closeRiskDisclosure}
                className="px-6 py-3 bg-gray-700 rounded hover:bg-gray-600 w-32"
              >
                Cancel
              </button>
              <button
                onClick={handleActivate}
                className="px-6 py-3 bg-purple-600 rounded hover:bg-purple-500 w-32"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      {showDisclaimerModal && (
        <AutomatedTradingDisclaimerModal limitTotalTrades={limitTotalTrades} />
      )}

      {/* Validation Popup */}
      {showValidationPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center px-4">
          <div className="bg-[#0F172A] text-white p-6 rounded-xl w-full max-w-md shadow-lg relative z-10 text-center">
            <h3 className="text-xl font-bold mb-4 text-red-400">⚠️ Incomplete Information</h3>
            <div className="mb-6 text-left">
              <pre className="whitespace-pre-wrap text-sm text-gray-300">{validationMessage}</pre>
            </div>
            <button
              onClick={() => setShowValidationPopup(false)}
              className="px-8 py-2 bg-red-600 rounded hover:bg-red-500 font-semibold"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Popup when all symbols are selected */}
      {showAllSymbolsSelectedPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center px-4">
          <div className="bg-[#0F172A] text-white p-6 rounded-xl w-full max-w-sm shadow-lg relative z-10 text-center">
            <h3 className="text-xl font-bold mb-4">Cannot Add More Symbols</h3>
            <p className="mb-6">Unable to select, must first press delete symbol.</p>
            <button
              onClick={() => setShowAllSymbolsSelectedPopup(false)}
              className="px-8 py-2 bg-purple-600 rounded hover:bg-purple-500 font-semibold"
            >
              agree
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AddWatchlistModal;
